import { Link } from "react-router-dom";
import {
  EnhancedSeoHead,
  FAQ,
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateFAQSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import { FileText } from "lucide-react";

const SOURCE_PATH = "/nya-socialtjanstlagen-hemtjanst";
const CANONICAL_URL = "https://nackahemtjanst.se" + SOURCE_PATH;

const FAQ_ITEMS = [
  {
    question: "När började den nya socialtjänstlagen gälla?",
    answer:
      "Den nya socialtjänstlagen (SFS 2025:400) trädde i kraft den 1 juli 2025.",
  },
  {
    question: "Får jag automatiskt hemtjänst enligt den nya lagen?",
    answer:
      "Nej. Om du behöver behovsprövad hemtjänst ansöker du fortfarande hos Nacka kommun och får dina individuella behov utredda.",
  },
  {
    question: "Kan Nacka erbjuda stöd utan individuell behovsprövning?",
    answer:
      "Lagen gör det möjligt för kommunen att erbjuda vissa personliga insatser utan individuell behovsprövning. Vilka insatser som erbjuds och hur du kontaktar kommunen behöver kontrolleras aktuellt hos Nacka kommun.",
  },
];

/** Local consumer guide explaining the new Social Services Act for Nacka residents. */
const NewSocialServicesAct = () => {
  const article = generateArticleSchema({
    headline: "Nya socialtjänstlagen och hemtjänsten i Nacka",
    description:
      "Lokal guide till vad den nya socialtjänstlagen betyder för dig som söker hemtjänst eller stöd i Nacka.",
    url: CANONICAL_URL,
    datePublished: "2026-08-05",
    dateModified: "2026-08-05",
  });
  const faq = generateFAQSchema(FAQ_ITEMS, CANONICAL_URL);
  const breadcrumb = generateBreadcrumbSchema([
    { name: "Hem", url: "https://nackahemtjanst.se/" },
    { name: "Nya socialtjänstlagen", url: CANONICAL_URL },
  ]);
  const schema = combineSchemas([article, faq, breadcrumb]);

  return (
    <>
      <EnhancedSeoHead
        title="Nya socialtjänstlagen och hemtjänst i Nacka"
        description="Vad betyder den nya socialtjänstlagen för dig som söker hemtjänst i Nacka? Läs om ansökan, förebyggande stöd och kommunens ansvar."
        canonicalUrl={CANONICAL_URL}
        ogType="article"
        keywords={[
          "nya socialtjänstlagen Nacka",
          "nya socialtjänstlagen hemtjänst Nacka",
          "hemtjänst Nacka lag",
          "hemtjänst utan behovsprövning Nacka",
        ]}
        contentClassification="Local Consumer Guide"
        targetAudience="seniors, relatives, Nacka residents"
        coverage="Nacka"
        publishDate="2026-08-05T00:00:00+02:00"
        modifiedDate="2026-08-05T00:00:00+02:00"
        author="Nacka Hemtjänst"
        schemaMarkup={schema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      <section className="bg-gradient-to-b from-amber-50 via-white to-white px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl text-center">
          <FileText
            className="mx-auto mb-6 h-12 w-12 text-amber-700"
            aria-hidden="true"
          />
          <p className="text-sm font-bold uppercase tracking-wider text-amber-700">
            Lokal guide för Nacka
          </p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-surface-900 md:text-5xl">
            Nya socialtjänstlagen och hemtjänsten i Nacka
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-xl leading-relaxed text-surface-600">
            Vad ändras för dig som behöver stöd hemma? Här förklarar vi vad
            lagen betyder, hur du ansöker och vad som behöver kontrolleras med
            Nacka kommun.
          </p>
        </div>
      </section>

      <div className="bg-white px-4 py-16">
        <article className="mx-auto max-w-4xl space-y-8 text-lg leading-8 text-surface-700">
          <nav aria-label="Brödsmulor" className="text-sm">
            <Link to="/" className="text-amber-700 hover:underline">
              Hem
            </Link>{" "}
            / Nya socialtjänstlagen
          </nav>

          <p className="text-xl leading-relaxed text-surface-600">
            Den nya socialtjänstlagen (SFS 2025:400) gäller sedan 1 juli 2025.
            Den ska göra socialtjänsten mer förebyggande, lättillgänglig och
            kunskapsbaserad. Lagen gäller i Nacka, men kommunen bestämmer hur
            lokala stöd och kontaktvägar organiseras.
          </p>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
            <p className="font-semibold text-amber-950">Kort svar</p>
            <p className="mt-2 text-amber-950">
              Du ansöker fortfarande om behovsprövad hemtjänst hos Nacka kommun.
              Vissa stödinsatser kan erbjudas utan individuell behovsprövning,
              men det är kommunen som avgör vad som finns.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-surface-900">
            Vad betyder lagen för dig?
          </h2>
          <p>
            Nacka kommun ska arbeta förebyggande och vara lätt att nå. Det kan
            innebära tydligare information, tidiga stödinsatser och fler vägar
            att komma i kontakt med socialtjänsten innan behoven blivit större.
          </p>

          <h2 className="text-3xl font-bold text-surface-900">
            Så ansöker du om hemtjänst i Nacka
          </h2>
          <ol className="list-decimal space-y-2 pl-6">
            <li>
              Kontakta Nacka kommun via e-tjänsten eller kontaktcenter och be om
              en socialsekreterare på äldreenheten.
            </li>
            <li>Beskriv vilka vardagsmoment du behöver hjälp med.</li>
            <li>Delta i kommunens utredning och invänta beslut.</li>
            <li>
              Välj godkänd utförare enligt Nackas aktuella kundvalsregler.
            </li>
          </ol>
          <p>
            Läs också vår guide om{" "}
            <Link to="/ansoka" className="text-amber-700 hover:underline">
              att ansöka om hemtjänst i Nacka
            </Link>
            .
          </p>

          <h2 className="text-3xl font-bold text-surface-900">
            Insatser utan individuell behovsprövning
          </h2>
          <p>
            Den nya lagen ger socialnämnden möjlighet att erbjuda vissa
            personliga insatser utan individuell behovsprövning. Det är inte en
            automatisk rätt till hemtjänst och alla insatser omfattas inte.
            Fråga Nacka kommun vad som gäller just nu och hur du kontaktar rätt
            verksamhet.
          </p>

          <h2 className="text-3xl font-bold text-surface-900">
            Officiell information
          </h2>
          <p>
            Den här sidan är en oberoende guide och inte en del av Nacka kommun.
            Kontrollera alltid aktuella regler och kontaktvägar hos kommunen.
          </p>
          <a
            href="https://www.nacka.se/omsorg-stod/senior/hjalp-i-hemmet/hemtjanst/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex font-semibold text-amber-700 hover:underline"
          >
            Nacka kommuns information om hemtjänst
          </a>

          <section className="border-t border-surface-200 pt-10">
            <h2 className="text-3xl font-bold text-surface-900">
              Vanliga frågor
            </h2>
            <div className="mt-6">
              <FAQ items={FAQ_ITEMS} />
            </div>
          </section>
        </article>
      </div>
    </>
  );
};

export default NewSocialServicesAct;
